import {
  type ColumnsType,
  ConfirmModal,
  DEFAULT_PAGE_SIZE,
  Flex,
  Loading,
  Table,
  TablePageSizeSelector,
  TableRowMenuCell,
  Text,
} from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import {
  useActivities,
  useDeleteActivity,
  useUpdateActivity,
} from '@/features/project/projects/detail/activities/_hooks/activity';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { TABLE_BUTTON_WIDTH } from '@/utils';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import { useProjectDetailDrawerState } from '../../../_components/ProjectDrawerStateProvider';
import { ActivityFormDrawer } from './ActivityFormDrawer';

type Props = {
  hasEditPermission: boolean;
};

export const ActivitiesTable: FC<Props> = ({ hasEditPermission }) => {
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { id: projectId } = useId();
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { mutateAsync: deleteActivityAsync } = useDeleteActivity();
  const [deleteActivityId, setDeleteActivityId] = useState<string | null>(null);
  const { mutateAsync: updateActivityAsync } = useUpdateActivity();
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data: activitiesData, isPending: isLoadingUseActivities } =
    useActivities({ projectId });

  const isActivityFormDrawerOpen =
    drawerState.open &&
    drawerState.type === 'activity' &&
    drawerState.isEditing;

  const editingActivity = activitiesData?.find(
    (activity) =>
      isActivityFormDrawerOpen && drawerState.activityId === activity.id,
  );

  if (!activitiesData || isLoadingUseActivities) {
    return <Loading />;
  }

  return (
    <>
      <Flex vertical gap={16}>
        <Flex justify="end" align="center">
          <TablePageSizeSelector
            defaultPageSize={pageSize}
            onChange={(newPageSize) => {
              setPageSize(newPageSize);
            }}
          />
        </Flex>
        <Table
          rowKey="id"
          dataSource={activitiesData}
          columns={[
            {
              key: 'sortOrder',
              dataIndex: 'sortOrder',
              render: (sortOrder) => <Text text={sortOrder} />,
              width: '60px',
            },
            {
              title: <Text text={t('activity-content')} />,
              key: 'activityContent',
              dataIndex: 'activityContent',
              render: (activityContent) => (
                <Flex style={{ width: '50%' }}>
                  <Text weight="regular" text={activityContent} />
                </Flex>
              ),
            },
            ...(hasEditPermission
              ? ([
                  {
                    title: '',
                    key: 'actions',
                    width: TABLE_BUTTON_WIDTH,
                    render: (_, record) => (
                      <PublishedProjectMutableGuard>
                        <TableRowMenuCell
                          items={[
                            {
                              key: 'edit',
                              label: t('edit'),
                              onClick: () => {
                                setDrawerState({
                                  open: true,
                                  type: 'activity',
                                  isEditing: true,
                                  activityId: record.id,
                                });
                              },
                            },
                            {
                              key: 'delete',
                              label: t('delete'),
                              danger: true,
                              onClick: () => {
                                setDeleteActivityId(record.id);
                              },
                            },
                          ]}
                        />
                      </PublishedProjectMutableGuard>
                    ),
                  },
                ] satisfies ColumnsType<(typeof activitiesData)[number]>)
              : []),
          ]}
          pagination={{
            showSizeChanger: false,
            position: ['bottomRight'],
            pageSize,
          }}
        />
      </Flex>
      {hasEditPermission && (
        <>
          <ActivityFormDrawer
            open={isActivityFormDrawerOpen}
            initialValues={{
              activityContent: editingActivity?.activityContent,
              sortOrder: editingActivity?.sortOrder,
            }}
            onSubmit={async (values) => {
              invariant(
                isActivityFormDrawerOpen && drawerState.activityId,
                t('unreachable-code'),
              );

              try {
                await updateActivityAsync({
                  projectId,
                  id: drawerState.activityId,
                  ...values,
                });

                await Promise.all([
                  openMessage('success', t('message-update-completed')),
                  (async () => {
                    setDrawerState({ open: false });
                  })(),
                ]);
              } catch {
                await openMessage('error', t('message-update-failed'));
              }
            }}
            onClose={() => {
              setDrawerState({ open: false });
            }}
          />
          <ConfirmModal
            skipCheck
            title={t('delete')}
            open={!!deleteActivityId}
            disclaimerText={t('delete-activity')}
            onCancel={() => {
              setDeleteActivityId(null);
            }}
            onOk={async () => {
              invariant(deleteActivityId, t('unreachable-code'));

              try {
                await deleteActivityAsync({
                  projectId,
                  id: deleteActivityId,
                });

                await Promise.all([
                  openMessage('success', t('message-delete-completed')),
                  (async () => {
                    setDeleteActivityId(null);
                  })(),
                ]);
              } catch {
                await openMessage('error', t('message-delete-failed'));
              }
            }}
          />
        </>
      )}
    </>
  );
};
